import React from "react";
import { osc, orbitAngle, wobble, stagger } from "../../core/AnimationUtils";

/* ================================================================
   Cosmic & Universe — 8 animated templates
   Black hole, nebula, supernova, comet, big bang, exoplanet, pulsar, dark matter
   Deep space visuals with particle and rotation animations
   ================================================================ */

// ── 1. Black Hole ────────────────────────────────────────────────────
export interface BlackHoleProps {
  opacity?: number;
  rotation?: number;
  /** Seconds since scene started — drives accretion disk rotation, jet flicker, lensing pulse. */
  t?: number;
}
export const BlackHole: React.FC<BlackHoleProps> = (props) => {
  const t = props.t ?? 0;

  // Accretion disk rotates continuously: one revolution every 4 seconds (faster inside)
  const diskAngle = orbitAngle(t, 4.0) * (180 / Math.PI);
  // Gravitational lensing ring pulses
  const lensR = osc(t, 2.8, 48, 56);
  // Jet flicker — opposing jets pulse opposite phase
  const jetOp = osc(t, 0.8, 0.2, 0.65);
  const jetOp2 = osc(t, 0.8, 0.2, 0.65, Math.PI); // opposite phase
  // Inner disk inner-glow
  const innerGlow = osc(t, 1.5, 0.4, 0.75);
  // Star twinkle phases
  const starPhases = Array.from({ length: 40 }, (_, i) => stagger(i, 0.22));

  return (
    <svg viewBox="0 0 800 500" style={{ width: "100%", opacity: props.opacity ?? 1 }}>
      <defs>
        <radialGradient id="bh-core"><stop offset="0%" stopColor="#000" /><stop offset="60%" stopColor="#0f172a" /><stop offset="100%" stopColor="#1e1b4b" stopOpacity="0" /></radialGradient>
        <radialGradient id="bh-disk"><stop offset="0%" stopColor="#f59e0b" stopOpacity="0.8" /><stop offset="50%" stopColor="#ef4444" stopOpacity="0.4" /><stop offset="100%" stopColor="#7c3aed" stopOpacity="0" /></radialGradient>
      </defs>
      {/* Stars — twinkling */}
      {Array.from({ length: 40 }).map((_, i) => {
        const baseOp = 0.3 + (i % 5) * 0.12;
        const twinkle = osc(t, 2.4 + (i % 6) * 0.3, baseOp * 0.35, baseOp, starPhases[i]);
        return (
          <circle key={i} cx={20 + (i * 97) % 760} cy={10 + (i * 53) % 480}
            r={0.5 + (i % 3) * 0.5} fill="#fff" opacity={twinkle} />
        );
      })}
      {/* Accretion disk — continuously rotating */}
      <g transform={`rotate(${diskAngle},400,250)`}>
        <ellipse cx="400" cy="250" rx="180" ry="50" fill="none" stroke="#f59e0b" strokeWidth="35" opacity="0.3" />
        <ellipse cx="400" cy="250" rx="160" ry="42" fill="none" stroke="#ef4444" strokeWidth="20" opacity="0.4" />
        <ellipse cx="400" cy="250" rx="140" ry="35" fill="none" stroke="#fbbf24" strokeWidth="8" opacity={innerGlow} />
      </g>
      {/* Event horizon */}
      <circle cx="400" cy="250" r="45" fill="url(#bh-core)" />
      <circle cx="400" cy="250" r="30" fill="#000" />
      {/* Gravitational lensing ring — oscillates */}
      <circle cx="400" cy="250" r={lensR} fill="none" stroke="#e0e7ff" strokeWidth="1.5" opacity="0.3" />
      {/* Jets — flicker with opposite phases */}
      <line x1="400" y1="200" x2="400" y2="30" stroke="#818cf8" strokeWidth="4" opacity={jetOp} />
      <line x1="400" y1="300" x2="400" y2="470" stroke="#818cf8" strokeWidth="4" opacity={jetOp2} />
      <text x="400" y="490" textAnchor="middle" fill="#a78bfa" fontSize="16" fontWeight="bold">Black Hole</text>
    </svg>
  );
};

// ── 2. Nebula Cloud ──────────────────────────────────────────────────
export interface NebulaCloudProps {
  opacity?: number;
  phase?: number;
  /** Seconds since scene started — drives nebula expansion, cloud drift, protostar pulsing. */
  t?: number;
}
export const NebulaCloud: React.FC<NebulaCloudProps> = (props) => {
  const t = props.t ?? 0;

  // Nebula layers drift and breathe — different periods for organic feel
  const drift1 = wobble(t, 8.0, 10);
  const drift2 = wobble(t, 6.5, 8, Math.PI * 0.6);
  const drift3 = wobble(t, 7.2, 6, Math.PI * 1.3);
  const rx1 = osc(t, 7.5, 190, 210);
  const ry2 = osc(t, 5.8, 95, 110);
  const rx3 = osc(t, 6.8, 115, 130);
  const op4 = osc(t, 4.8, 0.14, 0.26);
  // Star-forming knots pulse
  const starPhases = [0, 1.2, 2.5, 3.7];
  // Background star twinkle phases
  const bgStarPhases = Array.from({ length: 30 }, (_, i) => stagger(i, 0.26));

  return (
    <svg viewBox="0 0 800 500" style={{ width: "100%", opacity: props.opacity ?? 1 }}>
      {/* Background stars — twinkling */}
      {Array.from({ length: 30 }).map((_, i) => {
        const baseOp = 0.3 + (i % 4) * 0.1;
        const twinkle = osc(t, 3.0 + (i % 5) * 0.3, baseOp * 0.3, baseOp, bgStarPhases[i]);
        return (
          <circle key={i} cx={15 + (i * 79) % 770} cy={10 + (i * 47) % 480}
            r={0.8} fill="#fff" opacity={twinkle} />
        );
      })}
      {/* Nebula layers — drifting and breathing */}
      <ellipse cx={380 + drift1} cy={240} rx={rx1} ry="120" fill="#7c3aed" opacity="0.15" />
      <ellipse cx={420 + drift2} cy={250} rx="160" ry={ry2} fill="#ec4899" opacity="0.12" />
      <ellipse cx={400 + drift3} cy={230} rx={rx3} ry="80" fill="#3b82f6" opacity="0.18" />
      <ellipse cx={390 + drift1 * 0.4} cy={260} rx="80" ry="50" fill="#f472b6" opacity={op4} />
      {/* Embedded protostars — pulsing */}
      {[{ x: 350, y: 220 }, { x: 430, y: 260 }, { x: 400, y: 200 }, { x: 370, y: 270 }].map((s, i) => {
        const pr = osc(t, 2.0 + i * 0.5, 1.2, 4.0, starPhases[i]);
        const pop = osc(t, 2.0 + i * 0.5, 0.5, 1.0, starPhases[i]);
        return (
          <circle key={i} cx={s.x} cy={s.y} r={pr} fill="#fff" opacity={pop} />
        );
      })}
      <text x="400" y="430" textAnchor="middle" fill="#c4b5fd" fontSize="16" fontWeight="bold">Nebula — Stellar Nursery</text>
    </svg>
  );
};

// ── 3. Supernova Explosion ───────────────────────────────────────────
export interface SupernovaExplosionProps {
  opacity?: number;
  animationProgress?: number;
  /** Seconds since scene started — drives continuous shockwave expansion and particle motion. */
  t?: number;
}
export const SupernovaExplosion: React.FC<SupernovaExplosionProps> = (props) => {
  const animationProgress = props.animationProgress ?? 0;
  const t = props.t ?? 0;

  // Shockwave rings expand continuously with t; animationProgress seeds the initial size
  const baseExpand = 20 + animationProgress * 180;
  // Continuous pulsing expansion on top of animationProgress position
  const ringPulse = osc(t, 3.0, 0, 20);
  const expandR = baseExpand + ringPulse;
  // Core flicker — very fast
  const coreOp = osc(t, 0.28, 0.45, 1.0);
  // Shockwave stroke width breathes
  const strokeW = osc(t, 1.2, 1.5, 5.5);
  // Ejected particles: angle comes from index (deterministic), distance driven by t
  const particleColors = ["#ef4444", "#f59e0b", "#3b82f6"];

  return (
    <svg viewBox="0 0 800 500" style={{ width: "100%", opacity: props.opacity ?? 1 }}>
      {/* Shockwave rings — expanding and breathing */}
      <circle cx="400" cy="250" r={expandR} fill="none" stroke="#ef4444" strokeWidth={strokeW} opacity={Math.max(0, 1 - animationProgress * 0.7)} />
      <circle cx="400" cy="250" r={expandR * 0.7} fill="none" stroke="#f59e0b" strokeWidth="2" opacity={Math.max(0, 1 - animationProgress * 0.5)} />
      <circle cx="400" cy="250" r={expandR * 0.4} fill="none" stroke="#fbbf24" strokeWidth="4" opacity={Math.max(0, 1 - animationProgress * 0.3)} />
      {/* Core — flicker */}
      <circle cx="400" cy="250" r={Math.max(0.1, 15 - animationProgress * 10)} fill="#fff" opacity={coreOp} />
      {/* Ejected particles — travel outward with t, deterministic angles */}
      {Array.from({ length: 20 }).map((_, i) => {
        const angle = (i / 20) * Math.PI * 2;
        // Particles move outward as t increases, looping every 4 seconds
        const travelPhase = (t % 4.0) / 4.0;
        const baseDist = animationProgress * (120 + (i % 5) * 30);
        const dist = baseDist + travelPhase * 40;
        const pr = Math.max(0.1, 3 - animationProgress * 2 - travelPhase);
        const pop = Math.max(0, (1 - animationProgress) * (1 - travelPhase * 0.5));
        return (
          <circle key={i}
            cx={400 + Math.cos(angle) * dist}
            cy={250 + Math.sin(angle) * dist}
            r={pr}
            fill={particleColors[i % 3]}
            opacity={pop} />
        );
      })}
      <text x="400" y="470" textAnchor="middle" fill="#ef4444" fontSize="18" fontWeight="bold">Supernova</text>
    </svg>
  );
};

// ── 4. Comet Trail ───────────────────────────────────────────────────
export interface CometTrailProps {
  opacity?: number;
  animationProgress?: number;
  /** Seconds since scene started — drives comet travel, coma pulse, tail shimmer. */
  t?: number;
}
export const CometTrail: React.FC<CometTrailProps> = (props) => {
  const t = props.t ?? 0;

  // Comet travels continuously across the scene in a ~6s loop
  const loopT = (t % 6.0) / 6.0; // 0→1 every 6 seconds
  const cx = 100 + loopT * 600;
  const cy = 380 - Math.sin(loopT * Math.PI) * 250;
  // Coma glow pulsing
  const comaR = osc(t, 1.5, 20, 32);
  const comaOp = osc(t, 1.5, 0.1, 0.2);
  // Nucleus flicker
  const nucleusR = osc(t, 0.35, 6, 10);
  // Tail opacity shimmer
  const tailOp = osc(t, 2.5, 0.25, 0.4);
  // Star twinkle
  const starPhases = Array.from({ length: 25 }, (_, i) => stagger(i, 0.28));

  return (
    <svg viewBox="0 0 800 500" style={{ width: "100%", opacity: props.opacity ?? 1 }}>
      {/* Stars — twinkling */}
      {Array.from({ length: 25 }).map((_, i) => {
        const baseOp = 0.3 + (i % 4) * 0.1;
        const twinkle = osc(t, 2.5 + (i % 5) * 0.35, baseOp * 0.3, baseOp, starPhases[i]);
        return (
          <circle key={i} cx={(i * 83) % 800} cy={(i * 49) % 500}
            r="1" fill="#fff" opacity={twinkle} />
        );
      })}
      {/* Ion tail */}
      <path d={`M${cx},${cy} Q${cx - 80},${cy + 20} ${cx - 200},${cy + 50} Q${cx - 250},${cy + 55} ${cx - 300},${cy + 40}`}
        stroke="#60a5fa" strokeWidth="8" fill="none" opacity={tailOp} />
      <path d={`M${cx},${cy} Q${cx - 60},${cy + 10} ${cx - 150},${cy + 30}`}
        stroke="#93c5fd" strokeWidth="4" fill="none" opacity={tailOp * 1.3} />
      {/* Dust tail */}
      <path d={`M${cx},${cy} Q${cx - 70},${cy + 40} ${cx - 180},${cy + 80}`}
        stroke="#fbbf24" strokeWidth="6" fill="none" opacity={tailOp * 0.6} />
      {/* Coma glow */}
      <circle cx={cx} cy={cy} r={comaR} fill="#93c5fd" opacity={comaOp} />
      {/* Comet head */}
      <circle cx={cx} cy={cy} r="12" fill="#e0f2fe" />
      <circle cx={cx} cy={cy} r={nucleusR} fill="#fff" />
      <text x="400" y="480" textAnchor="middle" fill="#93c5fd" fontSize="16" fontWeight="bold">Comet</text>
    </svg>
  );
};

// ── 5. Big Bang ──────────────────────────────────────────────────────
export interface BigBangProps {
  opacity?: number;
  animationProgress?: number;
  /** Seconds since scene started — drives expanding shells, particle motion, galaxy spin. */
  t?: number;
}
export const BigBang: React.FC<BigBangProps> = (props) => {
  const animationProgress = props.animationProgress ?? 0;
  const t = props.t ?? 0;

  const era = animationProgress < 0.2 ? "singularity"
    : animationProgress < 0.5 ? "inflation"
    : animationProgress < 0.8 ? "particle"
    : "matter";

  // Rings expand with t on top of animationProgress seeding
  const ringPulse = osc(t, 2.0, 0, 15);
  // Core flash
  const coreOp = osc(t, 0.5, 0.75, 1.0);
  const coreR = 10 + (1 - animationProgress) * 30 + osc(t, 0.5, -2, 2);
  // Particle oscillation
  const particlePhases = Array.from({ length: 15 }, (_, i) => stagger(i, 0.3));
  // Galaxy rotation angles
  const galaxyAngles = [0, 1, 2, 3].map((i) => orbitAngle(t, 8 + i * 2, i * 0.8) * (180 / Math.PI));
  // Timeline dot bounce
  const dotBounce = wobble(t, 1.2, 2);

  return (
    <svg viewBox="0 0 800 500" style={{ width: "100%", opacity: props.opacity ?? 1 }}>
      {/* Expanding universe rings */}
      <circle cx="400" cy="250" r={animationProgress * 200 + ringPulse} fill="none" stroke="#f59e0b" strokeWidth="2" opacity="0.3" />
      <circle cx="400" cy="250" r={animationProgress * 150 + ringPulse * 0.7} fill="none" stroke="#ef4444" strokeWidth="3" opacity="0.4" />
      <circle cx="400" cy="250" r={animationProgress * 100 + ringPulse * 0.4} fill="none" stroke="#fbbf24" strokeWidth="4" opacity="0.5" />
      {/* Core flash */}
      <circle cx="400" cy="250" r={Math.max(0.1, coreR)} fill="#fff" opacity={coreOp * (1 - animationProgress * 0.7)} />
      {/* Particles forming — drift outward */}
      {animationProgress > 0.3 && Array.from({ length: 15 }).map((_, i) => {
        const angle = (i / 15) * Math.PI * 2;
        const baseDist = (animationProgress - 0.3) * 250;
        const drift = wobble(t, 3.5 + (i % 4) * 0.5, 8, particlePhases[i]);
        const pr = osc(t, 1.0 + i * 0.2, 1.5, 4.0, particlePhases[i]);
        return (
          <circle key={i}
            cx={400 + Math.cos(angle) * (baseDist + drift)}
            cy={250 + Math.sin(angle) * (baseDist + drift)}
            r={pr}
            fill={i % 3 === 0 ? "#ef4444" : i % 3 === 1 ? "#3b82f6" : "#10b981"}
            opacity="0.85" />
        );
      })}
      {/* Galaxy formations — rotate with t */}
      {animationProgress > 0.7 && [
        { x: 250, y: 180 }, { x: 550, y: 320 }, { x: 350, y: 350 }, { x: 500, y: 150 }
      ].map((g, i) => (
        <ellipse key={i} cx={g.x} cy={g.y} rx="20" ry="8" fill="#a78bfa" opacity="0.4"
          transform={`rotate(${galaxyAngles[i]},${g.x},${g.y})`} />
      ))}
      {/* Timeline */}
      <rect x="150" y="440" width="500" height="4" rx="2" fill="#334155" />
      <circle cx={150 + animationProgress * 500} cy={442 + dotBounce} r="6" fill="#fbbf24" />
      <text x={150 + animationProgress * 500} y="470" textAnchor="middle" fill="#94a3b8" fontSize="11">{era}</text>
      <text x="400" y="30" textAnchor="middle" fill="#fbbf24" fontSize="18" fontWeight="bold">The Big Bang</text>
    </svg>
  );
};

// ── 6. Exoplanet Transit ─────────────────────────────────────────────
export interface ExoplanetTransitProps {
  opacity?: number;
  animationProgress?: number;
  /** Seconds since scene started — drives continuous planet transit and star pulse. */
  t?: number;
}
export const ExoplanetTransit: React.FC<ExoplanetTransitProps> = (props) => {
  const t = props.t ?? 0;

  // Planet travels continuously: one transit every 8 seconds
  const loopT = (t % 8.0) / 8.0; // 0→1 every 8s
  const planetX = 100 + loopT * 600;
  const isTransiting = planetX > 330 && planetX < 470;

  // Star breathes
  const starR = osc(t, 3.0, 78, 82);
  const starGlowR = osc(t, 3.0, 85, 95);
  const starOp = isTransiting ? osc(t, 0.5, 0.65, 0.75) : 1.0;

  return (
    <svg viewBox="0 0 800 500" style={{ width: "100%", opacity: props.opacity ?? 1 }}>
      {/* Star */}
      <circle cx="400" cy="220" r={starGlowR} fill="#fbbf24" opacity="0.1" />
      <circle cx="400" cy="220" r={starR} fill="#fbbf24" opacity={starOp} />
      {/* Planet transiting */}
      <circle cx={planetX} cy="220" r="20" fill="#1e293b" />
      <circle cx={planetX + 3} cy="218" r="5" fill="#334155" opacity="0.5" />
      {/* Light curve (redraws as planet moves) */}
      <polyline
        points={`100,400 ${Math.min(planetX - 30, 330)},400 ${isTransiting ? `${planetX},430` : `${planetX},400`} ${Math.max(planetX + 30, 470)},400 700,400`}
        fill="none" stroke="#60a5fa" strokeWidth="2" />
      <rect x="100" y="380" width="600" height="60" fill="none" stroke="#334155" strokeWidth="1" />
      <text x="80" y="405" fill="#64748b" fontSize="10" textAnchor="end">Brightness</text>
      <text x="400" y="460" textAnchor="middle" fill="#64748b" fontSize="10">Time →</text>
      <text x="400" y="490" textAnchor="middle" fill="#60a5fa" fontSize="14" fontWeight="bold">Exoplanet Transit Method</text>
    </svg>
  );
};

// ── 7. Pulsar Spin ───────────────────────────────────────────────────
export interface PulsarSpinProps {
  opacity?: number;
  rotation?: number;
  /** Seconds since scene started — drives continuous pulsar rotation, beam sweep, field drift. */
  t?: number;
}
export const PulsarSpin: React.FC<PulsarSpinProps> = (props) => {
  const t = props.t ?? 0;

  // Pulsar spins very fast: one revolution every 0.6 seconds (realistic neutron star)
  const spinAngle = orbitAngle(t, 0.6) * (180 / Math.PI);
  // Neutron star radius pulses rapidly
  const starR = osc(t, 0.6, 23, 27);
  // Beam opacity — brightens as it sweeps toward viewer
  const beamOp = osc(t, 0.6, 0.25, 0.75);
  const beamOp2 = osc(t, 0.6, 0.25, 0.75, Math.PI); // opposite beam
  // Star twinkle
  const starPhases = Array.from({ length: 20 }, (_, i) => stagger(i, 0.32));

  return (
    <svg viewBox="0 0 800 500" style={{ width: "100%", opacity: props.opacity ?? 1 }}>
      {/* Stars background — twinkling */}
      {Array.from({ length: 20 }).map((_, i) => {
        const baseOp = 0.25 + (i % 4) * 0.08;
        const twinkle = osc(t, 2.8 + (i % 5) * 0.3, baseOp * 0.3, baseOp, starPhases[i]);
        return (
          <circle key={i} cx={(i * 91) % 800} cy={(i * 61) % 500}
            r="1" fill="#fff" opacity={twinkle} />
        );
      })}
      {/* Neutron star — pulsing */}
      <circle cx="400" cy="250" r={starR} fill="#a5b4fc" stroke="#818cf8" strokeWidth="2" />
      {/* Magnetic field lines — rotate with pulsar */}
      <g transform={`rotate(${spinAngle},400,250)`}>
        <ellipse cx="400" cy="250" rx="100" ry="40" fill="none" stroke="#6366f1" strokeWidth="1" opacity="0.35" strokeDasharray="4,4" />
        <ellipse cx="400" cy="250" rx="70" ry="30" fill="none" stroke="#818cf8" strokeWidth="1" opacity="0.45" strokeDasharray="4,4" />
      </g>
      {/* Beams — sweep with rotation */}
      <g transform={`rotate(${spinAngle},400,250)`}>
        <polygon points="400,225 380,50 420,50" fill="#60a5fa" opacity={beamOp} />
        <polygon points="400,275 380,450 420,450" fill="#60a5fa" opacity={beamOp2} />
      </g>
      {/* Rotation axis */}
      <line x1="400" y1="180" x2="400" y2="320" stroke="#94a3b8" strokeWidth="1" strokeDasharray="3,3" opacity="0.4" />
      <text x="400" y="470" textAnchor="middle" fill="#818cf8" fontSize="16" fontWeight="bold">Pulsar</text>
    </svg>
  );
};

// ── 8. Dark Matter Web ───────────────────────────────────────────────
export interface DarkMatterWebProps {
  opacity?: number;
  phase?: number;
  /** Seconds since scene started — drives filament shimmer, cluster pulse, halo breathing. */
  t?: number;
}
export const DarkMatterWeb: React.FC<DarkMatterWebProps> = (props) => {
  const t = props.t ?? 0;

  const nodes = [
    { x: 200, y: 150 }, { x: 400, y: 100 }, { x: 600, y: 160 },
    { x: 150, y: 300 }, { x: 400, y: 280 }, { x: 650, y: 300 },
    { x: 250, y: 420 }, { x: 500, y: 400 }, { x: 700, y: 430 },
  ];
  const edges = [[0,1],[1,2],[0,3],[1,4],[2,5],[3,4],[4,5],[3,6],[4,7],[5,8],[6,7],[7,8],[0,4],[2,4]];

  // Each filament pulses at its own phase
  const filamentPhases = edges.map((_, i) => stagger(i, 0.35));
  // Node cluster pulse phases
  const nodePhases = nodes.map((_, i) => stagger(i, 0.55));
  // Halo breathing phases
  const haloPhases = [0, 1.8, 3.5];

  return (
    <svg viewBox="0 0 800 500" style={{ width: "100%", opacity: props.opacity ?? 1 }}>
      {/* Filaments — shimmer independently */}
      {edges.map(([a, b], i) => {
        const filamentOp = osc(t, 3.5 + (i % 4) * 0.4, 0.08, 0.32, filamentPhases[i]);
        return (
          <line key={i}
            x1={nodes[a].x} y1={nodes[a].y}
            x2={nodes[b].x} y2={nodes[b].y}
            stroke="#6366f1" strokeWidth="2" opacity={filamentOp} />
        );
      })}
      {/* Galaxy clusters at nodes — pulse */}
      {nodes.map((n, i) => {
        const outerR = osc(t, 2.5 + (i % 4) * 0.35, 10, 16, nodePhases[i]);
        const innerR = osc(t, 2.5 + (i % 4) * 0.35, 5, 9, nodePhases[i]);
        return (
          <g key={i}>
            <circle cx={n.x} cy={n.y} r={outerR} fill="#7c3aed" opacity="0.15" />
            <circle cx={n.x} cy={n.y} r={innerR} fill="#a78bfa" opacity="0.45" />
            <circle cx={n.x} cy={n.y} r="2" fill="#e0e7ff" />
          </g>
        );
      })}
      {/* Dark matter halos — breathe */}
      {nodes.filter((_, i) => i % 3 === 0).map((n, i) => {
        const haloR = osc(t, 4.5, 25, 36, haloPhases[i]);
        return (
          <circle key={`h${i}`} cx={n.x} cy={n.y} r={haloR}
            fill="none" stroke="#4f46e5" strokeWidth="1" strokeDasharray="3,5" opacity="0.22" />
        );
      })}
      <text x="400" y="480" textAnchor="middle" fill="#818cf8" fontSize="14" fontWeight="bold">Dark Matter — Cosmic Web</text>
    </svg>
  );
};
